import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { api, errMsg, eur } from './api';
import { usePortal } from './PortalAuth';
import { goldBtn, ghostBtn } from './ui';

export const TEMPLATE_INFO = {
  complete: 'Score e limite di credito, anagrafica, bilanci 5 anni, gruppo societario, protesti, procedure concorsuali, pregiudizievoli, dirigenti e soci, informazioni aggiuntive',
  bundle: 'Report azienda + reputational check sul legale rappresentante, uniti in un Dossier PDF creato automaticamente',
};
const TITLES = { complete: 'Report azienda', bundle: 'Dossier rapido' };

export function usePrices() {
  const [prices, setPrices] = useState(null);
  useEffect(() => { api.get('/cs/prices').then((r) => setPrices(r.data)).catch(() => {}); }, []);
  return prices;
}

export default function PurchaseDialog({ company, open, onClose, onDone, defaultTemplate = 'complete', lockTemplate = false }) {
  const prices = usePrices();
  const { user, refresh } = usePortal();
  const [template, setTemplate] = useState(defaultTemplate);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  useEffect(() => { setTemplate(defaultTemplate); }, [defaultTemplate, open]);

  const foreign = (company?.id || '').slice(0, 2).toUpperCase() !== 'IT' && (prices?.report_foreign?.cents || 0) > 0;
  const priceOf = (t) => (t === 'bundle' ? prices?.bundle_report_aml?.cents : (foreign ? prices?.report_foreign?.cents : prices?.[`report_${t}`]?.cents));
  const extra = prices?.aml_check?.cents || 0;
  const cents = priceOf(template);
  const insufficient = cents != null && user && user.wallet_cents < cents;

  const buy = async () => {
    setBusy(true);
    try {
      if (template === 'bundle') {
        const { data } = await api.post('/dossier/quick', { company_id: company.id, company_name: company.name });
        toast.success(`Dossier rapido avviato · ${eur(data.charged_cents)} scalati dal plafond`);
        if (data.warning) toast.warning(data.warning);
        await refresh();
        onClose();
        navigate('/portal/dossier');
        return;
      }
      const { data } = await api.post('/cs/reports', { company_id: company.id, template, language: 'it', company_name: company.name });
      toast.success(`Report acquistato · ${eur(cents)} scalati dal plafond`);
      await refresh();
      onDone?.(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0B0D10] border-[#1F242C] text-[#E5E5E5] rounded-sm max-w-lg" data-testid="purchase-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Acquista report</DialogTitle>
          <DialogDescription className="text-[#8A8F97]">{company?.name} · {company?.id}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1">
          {Object.keys(TEMPLATE_INFO).filter((t) => (lockTemplate ? t === defaultTemplate : !(t === 'bundle' && foreign))).map((t) => (
            <button key={t} onClick={() => setTemplate(t)} data-testid={`template-option-${t}`}
              className={`w-full text-left p-4 border rounded-sm transition-colors ${template === t ? 'border-[#D4AF37] bg-[#D4AF37]/5' : 'border-[#1F242C] hover:border-[#3A3F47]'}`}>
              <div className="flex justify-between items-center mb-1">
                <span className="font-medium">{TITLES[t]}</span>
                <span className="font-mono text-sm text-[#D4AF37]">{prices ? eur(priceOf(t)) : '…'}</span>
              </div>
              <p className="text-xs text-[#7A7F87]">{TEMPLATE_INFO[t]}</p>
              {t === 'bundle' && extra > 0 && <p className="text-xs text-[#D4AF37]/80 mt-1" data-testid="bundle-extra-note">Primo legale rappresentante incluso · {eur(extra)} per ogni rappresentante aggiuntivo</p>}
            </button>
          ))}
        </div>
        <div className="flex items-center justify-between pt-2 text-sm">
          <span className="text-[#7A7F87]">Plafond disponibile</span>
          <span className="font-mono" data-testid="purchase-balance">{eur(user?.wallet_cents)}</span>
        </div>
        {template === 'bundle' && foreign && <p className="text-xs text-[#E37A72]" data-testid="bundle-foreign">Il dossier rapido è disponibile solo per aziende italiane.</p>}
        {insufficient && <p className="text-xs text-[#E37A72]" data-testid="purchase-insufficient">Plafond insufficiente: ricarica dalla sezione Plafond.</p>}
        <div className="flex gap-3 justify-end">
          <Button onClick={onClose} className={ghostBtn} data-testid="purchase-cancel">Annulla</Button>
          {insufficient ? (
            <Button onClick={() => { window.location.href = '/portal/plafond'; }} className={goldBtn} data-testid="purchase-topup">Ricarica plafond</Button>
          ) : (
            <Button onClick={buy} disabled={busy || cents == null || (template === 'bundle' && foreign)} className={goldBtn} data-testid="purchase-confirm">
              {busy ? <Loader2 className="animate-spin" size={16} /> : `Conferma · ${cents != null ? eur(cents) : ''}`}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
